import React, { useEffect, useState } from "react";
import { formatMoney, useGetPaymentCurrencyList } from "../../../utils";
import BudgetModal from "../../common/BudgetModal";
import { useTranslation } from "react-i18next";
import NewTooltips from "../../../assets/icons/NewTooltips";
import { useBudgetStore } from "../../../state/useBudgetStore";
import { useCompanyStore } from "../../../state/useCompanyStore";
import TooltipDropdown from "../../common/tooltipDropdown";

export interface BudgetHistoryWidgetProps {
  toolTipText?: string;
  width?: string; // Add a width prop
  height?: string; // Add a height prop
}

const BudgetHistory: React.FC<BudgetHistoryWidgetProps> = ({
  toolTipText,
  width,
  height,
}) => {
  const { t } = useTranslation("dashboard");
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );
  const {
    budgets,
    fetchBudgets,
    isBudgetLoading: isLoading,
    error,
  } = useBudgetStore();
  const [actualExpensePercentage, setActualExpensePercentage] = useState<
    null | number
  >(null);
  const [monthlyBudgetPercentage, setMonthlyBudgetPercentage] = useState<
    null | number
  >(null);
  const [isHovered, setIsHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const monthlyBudget = budgets[0]?.allocatedBudget ?? null;
  const actualExpenses = budgets[0]?.actualExpenses ?? null;

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleOnClick = () => {
    setIsHovered(!isHovered);
  };

  const formatValue = (value: number) => {
    return value.toLocaleString();
  };

  const containerStyles = {
    width: width || "100%", // Apply the width prop or fallback to 100%
    height: height || "100%",
  };

  useEffect(() => {
    fetchBudgets();
  }, [showModal]);

  useEffect(() => {
    if (actualExpenses !== null && monthlyBudget !== null) {
      let percentage = 0;

      if (monthlyBudget > 0) {
        percentage = (actualExpenses / monthlyBudget) * 100;
      }

      // Allow overspending to exceed 100%
      const actualExpensePercentage = percentage;
      const monthlyBudgetPercentage = Math.max(100 - percentage, 0); // Avoid negative values

      setActualExpensePercentage(actualExpensePercentage);
      setMonthlyBudgetPercentage(monthlyBudgetPercentage);
    }
  }, [actualExpenses, monthlyBudget]);
  if (error) {
    return <div>Error: {error}</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (budgets.length == 0) {
    return (
      <>
        <div
          className="p-2 border border-[#EAECF0] rounded-lg h-full"
          style={containerStyles}
        >
          <div className="flex justify-between items-center mb-5 lg:mb-7">
            <div className="font-normal flex items-center gap-2">
              <h2
                className="text-[#344054] text-[14px] font-medium leading-5"
                style={{ padding: "0" }}
              >
                Budget
              </h2>
              {toolTipText && (
            <div
              className="relative cursor-pointer"
              onClick={handleOnClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <TooltipDropdown toolTipText={toolTipText} isHovered={isHovered} />
            </div>
          )}
            </div>
          </div>
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="text-[#044E97] font-semibold text-[14px]  lg:pb-0  pb-9 "
            >
              Set a budget
            </button>
          </div>
        </div>

        {showModal && <BudgetModal setShowModal={setShowModal} />}
      </>
    );
  }

  const values = actualExpenses && monthlyBudget;
  return (
    <>
    <div
      className="p-2 border border-[#EAECF0] rounded-lg h-full"
      style={containerStyles}
    >
      <div className="flex justify-between items-center">
        <div className="font-normal flex items-center gap-2">
          <h2 className="lg:text-[#000000] text-[14px]">Budget</h2>
          {toolTipText && (
            <div
              className="relative cursor-pointer"
              onClick={handleOnClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <TooltipDropdown toolTipText={toolTipText} isHovered={isHovered} />
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="text-[#044E97] bg-[#e1eefe] font-semibold text-[14px] px-2 py-1.5 rounded-md"
        >
          Update Budget
        </button>
      </div>

      <div className="rounded-md mt-5">
        <div className="mt-[2px] flex items-center justify-between w-full mb-3">
          {monthlyBudget ? (
            <>
              <div
                className="flex-grow-0 bg-[#D82020] w-[100%] h-[4px] border-r border-[#D82020]"
                style={{
                  width: values
                    ? `${actualExpensePercentage}%`
                    : actualExpenses
                      ? "100%"
                      : 0,
                }}
              />
              <div
                className="flex-grow-0 bg-[#039855] w-[100%] h-[4px]"
                style={{
                  width: values
                    ? `${monthlyBudgetPercentage}%`
                    : monthlyBudget
                      ? "100%"
                      : 0,
                }}
              />
            </>
          ) : (
            <div
              className="flex-grow-0 bg-[#D82020] w-[100%] h-[7px] rounded-md"
              style={{ width: "100%" }}
            />
          )}
        </div>

        <div className="flex justify-between items-center">
          <div className="text-[14px]">
            <h1 className="text-[#828282] font-medium leading-4">
              Actual Expense
            </h1>
            <div
              className={`font-semibold leading-4 text-[16px] mt-1.5 text-[#D82020] ${
                !monthlyBudget ? "text-[#D82020]" : ""
              }`}
            >
              {`${userCurrencyCode.currency} ${formatValue(actualExpenses || 0)}`}
              <span className="text-gray-600 text-[14px]">
                {actualExpensePercentage !== null &&
                  actualExpensePercentage > 0 &&
                  ` (${Math.round(actualExpensePercentage)}%)`}
              </span>
            </div>
          </div>

          <div className="text-[#039855] text-end text-[14px]">
            <h1 className="text-[#828282] font-medium leading-4">
              {t("monthlyBudget")}
            </h1>
            <div className="font-semibold leading-4 text-[16px] mt-1.5">
              {monthlyBudget
                ? `${formatMoney(monthlyBudget, userCurrencyCode.currency)}`
                : `${formatMoney(0, userCurrencyCode.currency)}`}
            </div>
          </div>
        </div>
      </div>
    </div>
    {showModal && <BudgetModal setShowModal={setShowModal} />}
  </>
  );
};

export default BudgetHistory;
